import { AxiosResponse, AxiosError, AxiosRequestConfig, CanceledError } from 'axios'
import { initializeGuestUser, unauthorizeLogout } from '../context/UserAuthentication/actions';
import { getRequestId, getUser, isGuestUser } from '../helpers/helperMethods';
import store from '../redux/store/store';
import { bugSnagNotifyAPIError } from '../helpers/bugSnagHelper';
import { isTokenExpired, refreshToken } from '../helpers/authentication';
import { AUTH_USER, GUEST_USER, ROUTE_HOME, ROUTE_LOGIN, ROUTE_MAINTENANCE, ROUTE_THANK_YOU } from '../constants';

const { dispatch } = store;
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
//Queue to manage request promises, created while the token is being fetched
type QueuedRequest = {
  resolve: (token: any) => void;
  reject: (error: Error) => void;
};
let requestPromiseQueue: QueuedRequest[] = [];

function createAuthCancelError(message: string) {
  return new CanceledError(message);
}


function redirectToLogin() {
  if (window.location.pathname !== ROUTE_LOGIN) {
    window.location.assign(ROUTE_LOGIN);
  }
}
//Function that will resolve all the promises queued, after new token being fetched
function onRefreshed(token: any) {
  requestPromiseQueue.forEach(({ resolve }) => resolve(token));
  requestPromiseQueue = [];
}

function rejectQueuedRequests(message: string) {
  const error = createAuthCancelError(message);
  requestPromiseQueue.forEach(({ reject }) => reject(error));
  requestPromiseQueue = [];
}

function queueRefreshToken(resolve: (token: any) => void, reject: (error: Error) => void) {
  requestPromiseQueue.push({ resolve, reject });
}

export const requestHandler: any = async (request: AxiosRequestConfig) => {
  let authInfo = getUser();
  const { shouldRefetchToken: shouldRefresh, shouldLogout } = await isTokenExpired(authInfo);
  if ((!(getUser())?.hasOwnProperty('type') || isGuestUser(getUser())) && shouldLogout) {
    rejectQueuedRequests("Guest session expired");
    initializeGuestUser(dispatch);
    return Promise.reject(createAuthCancelError("Guest session expired"));
  }
  let requestId = getRequestId();

  // If both tokens (refreshed + auth) are expired
  if (shouldLogout) {
    rejectQueuedRequests("Session expired");
    return Promise.reject(createAuthCancelError("Session expired"));
    const isAuthenticatedUser = !isGuestUser(authInfo);
    unauthorizeLogout(dispatch);
    if (isAuthenticatedUser) {
      redirectToLogin();
    }
  }
  //code block to fetch new token and queue rest requests
  if (shouldRefresh) {
    if (!isRefreshing && !refreshPromise) {
      isRefreshing = true;
      const currentAuthUser = getUser();
      refreshPromise = refreshToken(dispatch, currentAuthUser)
        .then((newToken) => {
          if (!newToken?.token) {
            rejectQueuedRequests("Session expired");
            return;
          }
          onRefreshed(newToken);
          return newToken;
        })
        .catch(() => {
          rejectQueuedRequests("Session expired");
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
    }
    // [IMPORTANT] - This code block will create new promise against each async request and push them in queue,
    // they will be resolved once they get new refreshed tokens, or rejected if refresh/logout fails.
    const newUserTokens = await new Promise<any>((resolve, reject) => {
      queueRefreshToken(resolve, reject);
    });
    if (newUserTokens?.token) {
      request.headers['Authorization'] = `Bearer ${newUserTokens.token}`
      request.headers['token'] = newUserTokens.token;
      request.headers['Refresh-token'] = newUserTokens.refresh_token;
    }
  }
  else {
    request.headers['Authorization'] = request.headers['Authorization'] || 'Bearer ' + authInfo.token;
    request.headers['token'] = request.headers['token'] || authInfo.authentication_token;
    request.headers['Refresh-token'] = request.headers['Refresh-token'] || "";
  }
  request.headers['request-id'] = request.headers['request-id'] || requestId;
  return request;
};

export const successResponseHandler = (response: AxiosResponse) => {
  return response;
};

export const errorResponseHandler = (error: AxiosError) => {
  if (error?.response?.status === 401) {
    const user = getUser();
    const userType = isGuestUser(user)
      ? GUEST_USER
      : user?.id || user?.authentication_token || user?.token
        ? AUTH_USER
        : user?.visitor_id
          ? GUEST_USER
          : 'unknown';
    let payload = error.response.config?.data
      ? JSON.parse(error.response.config.data)
      : null;
    let apiInfo = {
      'Auth-token': error?.response?.config?.headers?.['Authorization'] || "",
      'Refresh-token': error?.response?.config?.headers?.['Refresh-token'] || "",
      'Payload': payload
    }
    rejectQueuedRequests("Session expired");
    const isAuthenticatedUser = !isGuestUser(user);
    unauthorizeLogout(dispatch);
    if (isAuthenticatedUser) {
      redirectToLogin();
    } else if (window.location.pathname === ROUTE_THANK_YOU) {
      window.location.assign(ROUTE_HOME);
    }

    bugSnagNotifyAPIError(
      "Authentication Error",
      apiInfo,
      `Request failed with status code 401 - user_type: ${userType}`
    );
  }
  // Handles Server Errors for Bugsnag reporting
  if (error?.response?.status >= 500 && error?.response?.status < 600) {
    bugSnagNotifyAPIError("Server Error occurred", error?.response, `Server Error with status code ${error?.response?.status}`);
  }

  if (error?.response?.status === 502) {
    if (window.location.pathname !== ROUTE_MAINTENANCE) {
      window.location.assign(ROUTE_MAINTENANCE);
    }
    return Promise.reject(error);
  }
  
  // Handles Network Errors for Bugsnag reporting
  if (!error?.response) {
    bugSnagNotifyAPIError("Network error occurred", error, error?.message);
  }
  return Promise.reject(error);
};